import type { ReactNode } from 'react';
import type { Vurdering } from '../api/types';
import { VURDERINGER_AKTIVERT } from '../lib/leverandorvurderinger';
import { Bildegalleri } from './Bildegalleri';
import { Stjerner, prisTekst } from './Leverandorkort';

/**
 * Annonsen, uten å vite hvor innholdet kom fra.
 *
 * To steder tegner den samme sida: brudeparets annonseside, som henter
 * oppføringen på id, og forhåndsvisningen et lokale får gjennom lenken sin.
 * De må se like ut – en forhåndsvisning som ikke er den ekte sida, er verre
 * enn ingen forhåndsvisning, for da retter man etter noe annet enn det folk
 * får se.
 *
 * Derfor eier denne komponenten utseendet, og sidene rundt eier dataene og
 * det som er forskjellig: tilbakelenka og vurderingsskjemaet hører til
 * brudeparet, ikke til lokalet.
 *
 * Stjernene i hodet og vurderingene nederst står bare når vurderingene er på –
 * se `lib/leverandorvurderinger`. Det skrus av her, og ikke i sidene rundt, så
 * forhåndsvisningen følger med av seg selv.
 */

/** Alt annonsen trenger. `LeverandorDetalj` passer som den er. */
export type Annonseinnhold = {
  navn: string;
  kategori: string;
  kortBeskrivelse: string;
  beskrivelse: string;
  adresse: string;
  postnummer: string;
  poststed: string;
  nettsted: string;
  epost: string;
  telefon: string;
  prisFra: number | null;
  snitt: number | null;
  antallVurderinger: number;
  bilder: string[];
  vurderinger: Vurdering[];
};

export function stedstekst(d: {
  adresse: string;
  postnummer: string;
  poststed: string;
}): string {
  return [d.adresse, [d.postnummer, d.poststed].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
}

export function Leverandorannonse({
  data,
  over,
  vurderingsskjema,
}: {
  data: Annonseinnhold;
  /** Det som står over bildene. Tilbakelenka på annonsesida. */
  over?: ReactNode;
  /** Brudeparets eget skjema. Utelatt i forhåndsvisningen: lokalet vurderer ikke seg selv. */
  vurderingsskjema?: ReactNode;
}) {
  const pris = prisTekst(data.prisFra);
  const sted = stedstekst(data);

  return (
    <div className="leverandorannonse">
      {over}

      <Bildegalleri bilder={data.bilder} navn={data.navn} />

      <header className="annonsehode">
        <div>
          <h1>{data.navn}</h1>
          {sted ? <p className="annonsested">{sted}</p> : null}
          {VURDERINGER_AKTIVERT ? (
            <Stjerner snitt={data.snitt} antall={data.antallVurderinger} />
          ) : null}
        </div>
        {pris ? <p className="annonsepris">{pris}</p> : null}
      </header>

      {data.kortBeskrivelse ? <p className="annonseingress">{data.kortBeskrivelse}</p> : null}

      <div className="annonsekropp">
        {data.beskrivelse ? (
          <section className="annonsetekst">
            {data.beskrivelse.split('\n').map((linje, i) =>
              linje.trim() ? <p key={i}>{linje}</p> : null,
            )}
          </section>
        ) : null}

        {data.nettsted || data.epost || data.telefon || sted ? (
          <aside className="annonsekontakt">
            <h2>Kontakt</h2>
            <ul>
              {sted ? <li>{sted}</li> : null}
              {data.nettsted ? (
                <li>
                  {/* Ut av vardag.no. `noreferrer` så leverandøren ikke får vite
                      hvilken side i planleggingen paret kom fra. */}
                  <a href={data.nettsted} target="_blank" rel="noopener noreferrer">
                    {data.nettsted.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </a>
                </li>
              ) : null}
              {data.epost ? (
                <li>
                  <a href={`mailto:${data.epost}`}>{data.epost}</a>
                </li>
              ) : null}
              {data.telefon ? (
                <li>
                  <a href={`tel:${data.telefon.replace(/\s/g, '')}`}>{data.telefon}</a>
                </li>
              ) : null}
            </ul>
          </aside>
        ) : null}
      </div>

      {VURDERINGER_AKTIVERT ? (
        <section className="vurderingsdel">
          <h2>Vurderinger</h2>
          {vurderingsskjema}

          {data.vurderinger.length === 0 ? (
            <p className="tomt">Ingen har vurdert denne ennå. Bli den første.</p>
          ) : (
            <ul className="vurderingsliste">
              {data.vurderinger.map((v) => (
                <li key={v.id}>
                  <Stjerner snitt={v.stjerner} antall={1} />
                  {v.tittel ? <h3>{v.tittel}</h3> : null}
                  {v.tekst ? <p>{v.tekst}</p> : null}
                  <time dateTime={v.opprettet}>
                    {new Date(v.opprettet).toLocaleDateString('nb-NO')}
                  </time>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}
    </div>
  );
}
